import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";

import parseCsv from "../utils/parseCsv";

export default function CsvImportDialog({ open, onClose, onImport, entityLabel = "records", importEndpoint = "", authHeaders, onImported, instructions = "" }) {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const pageSize = 5;
  const pageRows = useMemo(() => rows.slice(page * pageSize, page * pageSize + pageSize), [page, rows]);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));

  const reset = () => {
    setFileName(""); setHeaders([]); setRows([]); setPage(0); setProgress(0); setError("");
    if (inputRef.current) inputRef.current.value = "";
  };
  const close = () => { if (!progress) { reset(); onClose(); } };
  const chooseFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const parsed = parseCsv(await file.text());
      if (!parsed.rows.length) throw new Error("The CSV has no importable rows.");
      setFileName(file.name); setHeaders(parsed.headers); setRows(parsed.rows); setPage(0); setError("");
    } catch (nextError) { setRows([]); setHeaders([]); setFileName(""); setError(nextError.message || "Could not read this CSV file."); }
  };
  const submit = async () => {
    if (!rows.length || progress) return;
    if (!onImport && !importEndpoint) { setError("Import is not configured for this page."); return; }
    setError(""); setProgress(5);
    try {
      const result = onImport
        ? await onImport(rows, setProgress)
        : await (async () => {
          setProgress(15);
          const savedToken = localStorage.getItem("token") || localStorage.getItem("authToken") || sessionStorage.getItem("token") || sessionStorage.getItem("authToken") || "";
          const resolvedHeaders = typeof authHeaders === "function" ? authHeaders() : authHeaders;
          const response = await fetch(importEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(resolvedHeaders || {}), ...(savedToken ? { Authorization: `Bearer ${savedToken.replace(/^Bearer\s+/i, "")}`, "x-access-token": savedToken } : {}) },
            credentials: "include",
            body: JSON.stringify({ rows }),
          });
          setProgress(85);
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(payload?.error || "CSV import failed.");
          try { await onImported?.(payload); }
          catch { console.warn("CSV imported successfully; reload the page to see the updated records."); }
          return payload;
        })();
      setProgress(100);
      window.setTimeout(() => { reset(); onClose(); }, 350);
      return result;
    } catch (nextError) { setProgress(0); setError(nextError.message || "CSV import failed."); }
  };

  return <Dialog open={open} onClose={close} fullWidth maxWidth="md" PaperProps={{ sx: { width: 840, maxWidth: "calc(100vw - 32px)" } }}>
    <DialogTitle data-popup-header sx={{ fontSize: 18, fontWeight: 700 }}>Import CSV</DialogTitle>
    <DialogContent sx={{ pt: "12px !important" }}>
      <Typography sx={{ color: "#667085", fontSize: 13, mb: 1.25 }}>Select a CSV to preview and import {entityLabel}. Existing rows are updated when their ID or matching identity is found.</Typography>
      {instructions && <Alert severity="info" sx={{ mb: 1.25 }}>{instructions}</Alert>}
      <input ref={inputRef} type="file" accept=".csv,text/csv" hidden onChange={chooseFile} />
      <Button startIcon={<UploadFileOutlinedIcon />} variant="outlined" onClick={() => inputRef.current?.click()} disabled={Boolean(progress)} sx={{ textTransform: "none", mb: 1.25 }}>Select CSV file</Button>
      {fileName ? <Typography sx={{ display: "inline-block", ml: 1, color: "#475467", fontSize: 13 }}>{fileName} · {rows.length} row{rows.length === 1 ? "" : "s"}</Typography> : null}
      {progress ? <Box sx={{ mb: 1.25 }}><LinearProgress variant="determinate" value={progress} /><Typography sx={{ fontSize: 12, color: "#667085", mt: .5 }}>Importing… {progress}%</Typography></Box> : null}
      {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
      {rows.length ? <>
        <TableContainer sx={{ maxHeight: 310, overflow: "auto", border: "1px solid #EAECF0", borderRadius: 1 }}>
          <Table size="small" sx={{ minWidth: Math.max(720, headers.length * 160), tableLayout: "fixed", "& .MuiTableCell-root": { boxSizing: "border-box", lineHeight: 1.35 }, "& .MuiTableBody-root .MuiTableRow-root": { height: "auto !important" }, "& .MuiTableBody-root .MuiTableCell-root": { height: 38 } }}>
            <TableHead><TableRow>{headers.map((header) => <TableCell key={header} sx={{ minWidth: 160, py: .4, px: 1, fontWeight: 600, backgroundColor: "#F9FAFB" }}>{header}</TableCell>)}</TableRow></TableHead>
            <TableBody>{pageRows.map((row, rowIndex) => <TableRow key={rowIndex}>{headers.map((header) => <TableCell key={header} sx={{ minWidth: 160, py: .55, px: 1 }}><Typography noWrap title={row[header]} sx={{ fontSize: 12, color: "#667085", overflow: "hidden", textOverflow: "ellipsis" }}>{row[header] || "—"}</Typography></TableCell>)}</TableRow>)}</TableBody>
          </Table>
        </TableContainer>
        {rows.length > pageSize ? <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 1 }}><Button size="small" disabled={!page} onClick={() => setPage((value) => value - 1)}>Prev</Button><Typography sx={{ fontSize: 12, color: "#667085" }}>{page + 1} / {pageCount}</Typography><Button size="small" disabled={page >= pageCount - 1} onClick={() => setPage((value) => value + 1)}>Next</Button></Box> : null}
      </> : null}
    </DialogContent>
    <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}><Button onClick={close} disabled={Boolean(progress)} sx={{ textTransform: "none" }}>Cancel</Button><Button variant="contained" onClick={submit} disabled={!rows.length || Boolean(progress)} sx={{ textTransform: "none", backgroundColor: "#9747FF", "&:hover": { backgroundColor: "#9747FF" } }}>{progress ? "Importing…" : "Import CSV"}</Button></DialogActions>
  </Dialog>;
}
